import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { isPersonalReadonlyPortal, isTencentAdminPortal, isDecisionPortal } from './config/portalMode';
import analytics from './utils/analytics';
import ProductListPage from './pages/ProductListPage';
import ProductDetailPage from './pages/ProductDetailPage';
import TencentAdminApp from './pages/tencent-admin/TencentAdminApp';
import DecisionApp from './pages/decision-portal/DecisionApp';
import BClassMetaSettingsPage from './pages/BClassMetaSettingsPage';
import PostEstablishComparePage from './pages/PostEstablishComparePage';
import ProductSeriesComparePage from './pages/ProductSeriesComparePage';
import AdminPage from './pages/AdminPage';
import AdminLoginPage from './pages/AdminLoginPage';
import KnowledgePage from './pages/KnowledgePage';
import DailyReportPage from './pages/DailyReportPage';
import NewProductCalendarPage from './pages/NewProductCalendarPage';
import AssetsCenterPage from './pages/AssetsCenterPage';
import TradeCenterPage from './pages/TradeCenterPage';
import HoldingAssetsPage from './pages/HoldingAssetsPage';
import RedeemedHoldingsPage from './pages/RedeemedHoldingsPage';
import ProfitProductDetailPage from './pages/ProfitProductDetailPage';
import ProductTradesPage from './pages/ProductTradesPage';
import ProductSolicitationPage from './pages/ProductSolicitationPage';
import BondMarketBarometerPage from './pages/BondMarketBarometerPage';
import UserFeedbackPage from './pages/UserFeedbackPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import DisclaimerPage from './pages/DisclaimerPage';
import TermsPage from './pages/TermsPage';
import RedeemPage from './pages/RedeemPage';
// import ImportDataPage from './pages/ImportDataPage'; // 移除导入页面

// 计算器相关组件（新增功能，不影响现有逻辑）
import CalculatorHome from './pages/calculators/CalculatorHome';
import NavCalculator from './pages/calculators/NavCalculator';
import AmountCalculator from './pages/calculators/AmountCalculator';
import SevenDayCalculator from './pages/calculators/SevenDayCalculator';
import RateConverter from './pages/calculators/RateConverter';

// 临时添加：设备指纹测试组件（开发测试用）
import DeviceFingerprintTest from './components/DeviceFingerprintTest';
import LoginTest from './components/LoginTest';
import DemoPage from './components/DemoPage';
import MembershipPayTestPage from './pages/MembershipPayTestPage';

// 认证相关组件（新增功能，不影响现有逻辑）
import { AuthProvider } from './contexts/AuthContext';
import { LoginModalProvider, useLoginModal } from './contexts/LoginModalContext';
import { UserProfileModalProvider, useUserProfileModal } from './contexts/UserProfileModalContext';
import { AnnouncementProvider } from './contexts/AnnouncementContext';
import ProtectedRoute from './components/ProtectedRoute';
import UserProfile from './components/UserProfile';
import TopNavBar from './components/TopNavBar';
import Sidebar from './components/Sidebar';
import LoginModal from './components/LoginModal';
import AddToHomeScreen from './components/AddToHomeScreen';
import AnnouncementBanner from './components/AnnouncementBanner';
import { SidebarProvider } from './contexts/SidebarContext';
import { AssetsHubViewLayout } from './contexts/AssetsHubViewContext';

import './App.css';
import MaintenancePage from './pages/MaintenancePage';
import { useConfig } from './contexts/ConfigContext';
import AuthGuard from './components/AuthGuard';
import ErrorBoundary from './components/ErrorBoundary';
import {
  bootstrapCloudSync,
  installHoldingsChangeListener
} from './utils/assetHoldingCloudSync';
import { hasPendingListScrollRestore } from './utils/listNavigationState';
import ModalAnnouncementHost from './components/ModalAnnouncementHost';

/** 全局：任意页面 saveHoldings 都触发云同步预约；换号后自动拉一次云端 */
function HoldingsCloudSyncInstaller() {
  useEffect(() => {
    const uninstall = installHoldingsChangeListener();
    const onUserChanged = (event) => {
      const nextUserId = event?.detail?.nextUserId;
      if (!nextUserId) return;
      void bootstrapCloudSync().catch(() => {});
    };
    window.addEventListener('asset-storage-user-changed', onUserChanged);
    return () => {
      uninstall();
      window.removeEventListener('asset-storage-user-changed', onUserChanged);
    };
  }, []);
  return null;
}

// 全局登录模态框组件
const GlobalLoginModal = () => {
  const { isOpen, closeLoginModal, prefillUsername } = useLoginModal();

  return (
    <LoginModal
      isOpen={isOpen}
      onClose={closeLoginModal}
      initialUsername={prefillUsername}
    />
  );
};

// 全局用户中心模态框组件
const GlobalUserProfileModal = () => {
  const { isOpen, closeUserProfileModal, initialTab } = useUserProfileModal();
  
  return (
    <UserProfile 
      isOpen={isOpen}
      initialTab={initialTab}
      onClose={closeUserProfileModal} 
    />
  );
};


// 检查是否是已登录的管理员
const isAdminLoggedIn = () => {
  try {
    const adminToken = localStorage.getItem('admin_token');
    const adminUserData = localStorage.getItem('admin_user');
    
    if (!adminToken || !adminUserData) {
      return false;
    }
    
    const adminUser = JSON.parse(adminUserData);
    // 验证是admin用户
    return adminUser.username === 'admin';
  } catch (error) {
    return false;
  }
};

/** 腾讯云个人只读：仅产品列表 + 详情，走 finance-api /api（只读库） */
const PersonalReadonlyRoutes = () => (
  <>
    <TopNavBar />
    <Sidebar />
    <ErrorBoundary>
      <Routes>
        <Route
          path="/"
          element={
            <AuthGuard blur={false} requireAuth={false}>
              <ProductListPage />
            </AuthGuard>
          }
        />
        <Route
          path="/product/:id"
          element={
            <AuthGuard blur={false} requireAuth={false}>
              <ProductDetailPage />
            </AuthGuard>
          }
        />
        <Route path="/meta-settings" element={<BClassMetaSettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  </>
);

// 内部路由组件，用于检查维护模式
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // layout 阶段判断：须在列表页清掉 scroll 标记之前跳过，否则仍会滚到顶闪一下
  useLayoutEffect(() => {
    if ((pathname === '/' || pathname === '') && hasPendingListScrollRestore()) {
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const AppRoutes = () => {
  if (isDecisionPortal) {
    return <DecisionApp />;
  }

  if (isTencentAdminPortal) {
    return <TencentAdminApp />;
  }

  if (isPersonalReadonlyPortal) {
    return <PersonalReadonlyRoutes />;
  }

  const location = useLocation();
  const { loading, maintenanceEnabled, maintenanceMessage } = useConfig();
  
  // 管理员页面路径（不受维护模式影响）
  const isAdminPath = useMemo(() => location.pathname.startsWith('/admin'), [location.pathname]);
  const isAssetCenterPath = useMemo(() => (
    location.pathname.startsWith('/assets-center')
    || location.pathname.startsWith('/trade-center')
    || location.pathname.startsWith('/holding-assets')
    || location.pathname.startsWith('/profit-product')
  ), [location.pathname]);
  
  // 检查是否是已登录的管理员（路径变化时重新检查）
  const isAdmin = useMemo(() => isAdminLoggedIn(), [location.pathname]);
  
  // 维护模式拦截逻辑：
  // 1. 如果是管理员页面路径 → 允许访问（用于登录）
  // 2. 如果是已登录的管理员 → 允许访问所有页面
  // 3. 否则 → 显示维护页
  if (!loading && maintenanceEnabled && !isAdminPath && !isAdmin) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  return (
    <>
      <TopNavBar />
      <Sidebar />
      {!isAssetCenterPath && <AnnouncementBanner />}
      <ErrorBoundary>
        <Routes>
                <Route path="/" element={<AuthGuard blur={true} requireAuth={true}><ProductListPage /></AuthGuard>} />
                <Route path="/product/:id" element={<AuthGuard blur={true} requireAuth={true}><ProductDetailPage /></AuthGuard>} />
                <Route path="/post-establish" element={<AuthGuard blur={true} requireAuth={true}><PostEstablishComparePage /></AuthGuard>} />
                <Route path="/series-compare" element={<AuthGuard blur={true} requireAuth={true}><ProductSeriesComparePage /></AuthGuard>} />
                <Route path="/compare/:ids" element={<Navigate to="/post-establish" replace />} />
                <Route path="/compare" element={<Navigate to="/post-establish" replace />} />
                {/* <Route path="/admin/import" element={<ImportDataPage />} /> */}
                
                {/* 计算器相关路由 */}
                <Route path="/calculators" element={<AuthGuard blur={true} requireAuth={true}><CalculatorHome /></AuthGuard>} />
                <Route path="/calculators/nav-calculator" element={<AuthGuard blur={true} requireAuth={true}><NavCalculator /></AuthGuard>} />
                <Route path="/calculators/amount-calculator" element={<AuthGuard blur={true} requireAuth={true}><AmountCalculator /></AuthGuard>} />
                <Route path="/calculators/seven-day-calculator" element={<AuthGuard blur={true} requireAuth={true}><SevenDayCalculator /></AuthGuard>} />
                <Route path="/calculators/rate-converter" element={<AuthGuard blur={true} requireAuth={true}><RateConverter /></AuthGuard>} />
                
                {/* 管理员页面 */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminPage />} />
                
                {/* 知识库页面 */}
                <Route path="/knowledge" element={<AuthGuard blur={true} requireAuth={true}><KnowledgePage /></AuthGuard>} />
                <Route path="/knowledge/:id" element={<AuthGuard blur={true} requireAuth={true}><KnowledgePage /></AuthGuard>} />
                
                {/* 每日日报页面 */}
                <Route path="/daily-report" element={<AuthGuard blur={true} requireAuth={true}><DailyReportPage /></AuthGuard>} />
                <Route path="/daily-report/:id" element={<AuthGuard blur={true} requireAuth={true}><DailyReportPage /></AuthGuard>} />
                
                {/* 旧额度监控页已下线，保留路径避免书签 404 */}
                <Route path="/quota-monitor" element={<Navigate to="/" replace />} />
                
                {/* 新发产品日历页面 */}
                <Route path="/new-products-calendar" element={<AuthGuard blur={true} requireAuth={true}><NewProductCalendarPage /></AuthGuard>} />

                {/* 债市晴雨表 */}
                <Route path="/macro/bond" element={<AuthGuard blur={true} requireAuth={true}><BondMarketBarometerPage /></AuthGuard>} />
                <Route path="/macro" element={<Navigate to="/macro/bond" replace />} />
                
                {/* 资产中心：与产品收益详情共用内存层，返回时保留本次排序 */}
                <Route element={<AuthGuard blur={true} requireAuth={true}><AssetsHubViewLayout /></AuthGuard>}>
                  <Route path="/assets-center" element={<AssetsCenterPage />} />
                  <Route path="/profit-product" element={<ProfitProductDetailPage />} />
                </Route>
                <Route path="/trade-center" element={<AuthGuard blur={true} requireAuth={true}><TradeCenterPage /></AuthGuard>} />
                <Route path="/trade-center/product" element={<AuthGuard blur={true} requireAuth={true}><ProductTradesPage /></AuthGuard>} />
                <Route path="/trade-center/redeemed" element={<AuthGuard blur={true} requireAuth={true}><RedeemedHoldingsPage /></AuthGuard>} />
                <Route path="/holding-assets" element={<AuthGuard blur={true} requireAuth={true}><HoldingAssetsPage /></AuthGuard>} />
                <Route path="/product-solicitation" element={<AuthGuard blur={true} requireAuth={true}><ProductSolicitationPage /></AuthGuard>} />
                <Route path="/feedback" element={<AuthGuard blur={true} requireAuth={true}><UserFeedbackPage /></AuthGuard>} />
                
                {/* 法律信息页面 */}
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/disclaimer" element={<DisclaimerPage />} />
                <Route path="/terms" element={<TermsPage />} />

                {/* 公开领取账号页面（无需登录） */}
                <Route path="/redeem" element={<RedeemPage />} />
                
                {/* 功能演示页面 */}
                <Route path="/demo" element={<DemoPage />} />
                
                {/* 临时测试路由 - 开发完成后需要移除 */}
                <Route path="/dev/fingerprint-test" element={<DeviceFingerprintTest />} />
                <Route path="/dev/login-test" element={<LoginTest />} />
                {/* 自助续费联调：默认无人可用；仅开关+白名单用户能下单 */}
                <Route
                  path="/dev/pay-test"
                  element={(
                    <AuthGuard blur={false} requireAuth={true} allowExpiredMembership>
                      <MembershipPayTestPage />
                    </AuthGuard>
                  )}
                />
        </Routes>
      </ErrorBoundary>
      
      {/* 全局登录模态框 */}
      <GlobalLoginModal />
      
      {/* 全局用户中心模态框 */}
      <GlobalUserProfileModal />
      
      {/* 添加到桌面提示 */}
      <AddToHomeScreen />
    </>
  );
};

function App() {
  if (isDecisionPortal) {
    return (
      <Router>
        <div className="App decision-root">
          <DecisionApp />
        </div>
      </Router>
    );
  }

  if (isTencentAdminPortal) {
    // 产品列表/详情复用主站页面，内部 useAuth / useLoginModal，必须包 Provider，否则 /products 白屏
    return (
      <AuthProvider>
        <LoginModalProvider>
          <Router>
            <div className="App tadmin-root">
              <TencentAdminApp />
            </div>
          </Router>
        </LoginModalProvider>
      </AuthProvider>
    );
  }

  console.log('App component rendered');
  
  // 初始化GA4
  useEffect(() => {
    analytics.init();
  }, []);

  return (
    <AuthProvider>
      <AnnouncementProvider>
        <LoginModalProvider>
          <UserProfileModalProvider>
            <SidebarProvider>
              <Router>
                <ScrollToTop />
                <HoldingsCloudSyncInstaller />
                <ModalAnnouncementHost />
                <div className="App">
                  <AppRoutes />
                </div>
              </Router>
            </SidebarProvider>
          </UserProfileModalProvider>
        </LoginModalProvider>
      </AnnouncementProvider>
    </AuthProvider>
  );
}

export default App;
